import { createColumnHelper } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon, Popup } from "semantic-ui-react";

import { DATE_FORMATS_FNS, DetailsCard, Dropdown, ReactTable } from "components";
import { COMMUNICATION_CHANNELS, COMMUNICATION_RECEIVER_STATUSES } from "models";
import {
  AcsesReceiverCommunicationLog,
  CommunicationLog,
  DeskCommunicationLog,
  KeylockerCommunicationLog,
  KioskCommunicationLog
} from "modules/AppointmentDetails/components";
import "modules/AppointmentDetails/components/ActivityLog/ActivityLog.scss";
import { ActivityType } from "modules/AppointmentDetails/components/ActivityLog/components";
import { ACTIVITY_LOG_TYPE, getFilterOptions, useActivityLogs } from "modules/AppointmentDetails/components/ActivityLog/utils";
import { formatDate } from "utils/dateHelperFunctions";

import type { SectionMenu } from "components";
import type { Appointment, CommunicationReceiver } from "models";
import type { ActivityLogs } from "modules/AppointmentDetails/components/ActivityLog/utils";

type ActivityLogProps = {
  appointment: Appointment;
  isExpanded: boolean;
  sectionMenu: SectionMenu;
};

type CommunicationReceiverAndChannelType = {
  receiver: CommunicationReceiver;
};

const columnHelper = createColumnHelper<ActivityLogs>();

const CommunicationReceiverAndChannel = ({ receiver }: CommunicationReceiverAndChannelType) => {
  const { t } = useTranslation();

  const getChannelName = (channel: COMMUNICATION_CHANNELS) => {
    if (channel === COMMUNICATION_CHANNELS.Email) return t("v8_email", "Email");
    if (channel === COMMUNICATION_CHANNELS.SMS) return t("v8_sms", "SMS");
    if (channel === COMMUNICATION_CHANNELS.WhatsApp) return t("v8_whatsapp_message", "WhatsApp message");
    return "";
  };

  const channel = getChannelName(receiver.channel);

  if (receiver.status === COMMUNICATION_RECEIVER_STATUSES.Pending)
    return (
      <span>
        <Icon name="clock outline" className="i-yellow" /> {channel} {t("v8_is_pending", "is pending")}
      </span>
    );

  if (receiver.status === COMMUNICATION_RECEIVER_STATUSES.Success)
    return (
      <span>
        <Icon name="check circle" className="i-green" /> {channel} {t("v8_is_sent", "is sent")}
      </span>
    );

  return (
    <span className="communication-message-failed-container">
      <Icon name="exclamation circle" className="i-maroon" />{" "}
      <span>
        {channel} {t("v8_has_failed", "has failed")}
      </span>
      {receiver.reason && (
        <Popup
          position="left center"
          content={<div className="communication-message-failed-reason-content">{receiver.reason}</div>}
          trigger={<div className="ellipsis communication-message-failed-reason">{`: ${receiver.reason}`}</div>}
        />
      )}
    </span>
  );
};

export const ActivityLog = ({ isExpanded, sectionMenu, appointment }: ActivityLogProps) => {
  const { t } = useTranslation();

  const [activityTypeFilter, setActivityTypeFilter] = useState<ACTIVITY_LOG_TYPE>(ACTIVITY_LOG_TYPE.All);
  const activityLogs = useActivityLogs(appointment);

  const columns = useMemo(
    () => [
      columnHelper.accessor(row => row, {
        id: "activity",
        header: t("v8_activity", "Activity"),
        cell: ({ row: { original } }) => (
          <ActivityType
            activityType={original.activityType}
            statusIdentifier={original.statusIdentifier}
            isDeskCheckOut={original.isDeskCheckOut}
            keylockerPinRequestType={original.keylockerPinRequestType}
            activityTitle={original.activityTitle}
            event_type={original.eventType}
            appointmentLogType={original.appointmentLogType}
          />
        )
      }),
      columnHelper.accessor(row => row, {
        id: "userCustomer",
        header: t("v8_user_customer", "User / Customer"),
        cell: ({ row: { original } }) => original.name
      }),
      columnHelper.accessor(row => row, {
        id: "time",
        header: t("v8_time", "Time"),
        cell: ({ row: { original } }) => formatDate(original.time, original.isDateOnly ? DATE_FORMATS_FNS.dateMonthYear : DATE_FORMATS_FNS.dateMonthYearTime)
      }),
      columnHelper.accessor("note", {
        header: t("v8_note", "Note"),
        cell: ({ row: { original } }) => (original.receiver ? <CommunicationReceiverAndChannel receiver={original.receiver} /> : original.note)
      })
    ],
    [t]
  );

  if (!sectionMenu.count) return null;

  const filteredActivityLogs = activityTypeFilter === ACTIVITY_LOG_TYPE.All ? activityLogs : activityLogs.filter(l => l.activityType === activityTypeFilter);
  const activityTypeFilterOptions = getFilterOptions(activityLogs, t);

  const handleActivityTypeFilterChange = (activityLogType: ACTIVITY_LOG_TYPE = ACTIVITY_LOG_TYPE.All) => setActivityTypeFilter(activityLogType);

  return (
    <DetailsCard
      sectionMenuId={sectionMenu.id}
      title={sectionMenu.count ? `${sectionMenu.title} (${sectionMenu.count})` : sectionMenu.title}
      icon={sectionMenu.icon}
      isExpanded={isExpanded}
      rightContent={
        activityTypeFilterOptions.length > 2 ? (
          <div className="Activitylog-filter">
            <label>{t("v8_activity_type", "Activity Type")}</label>
            <Dropdown selectOnBlur={false} value={activityTypeFilter} options={activityTypeFilterOptions} selection onChange={handleActivityTypeFilterChange} />
          </div>
        ) : null
      }
    >
      {filteredActivityLogs.length > 0 && (
        <div className="mt-15">
          <ReactTable data={filteredActivityLogs} columns={columns} />
        </div>
      )}
      {!!appointment.customer_communication?.events?.length && (
        <div className="AppointmentDetails-cards mt-15">
          <CommunicationLog appointment={appointment} />
        </div>
      )}
      {!!appointment.desk_communications?.length && (
        <div className="AppointmentDetails-cards mt-15">
          <DeskCommunicationLog appointment={appointment} />
        </div>
      )}
      {!!appointment.kiosk_communications?.length && (
        <div className="AppointmentDetails-cards mt-15">
          <KioskCommunicationLog appointment={appointment} />
        </div>
      )}
      {!!appointment.keylocker_communications?.length && (
        <div className="AppointmentDetails-cards mt-15">
          <KeylockerCommunicationLog appointment={appointment} />
        </div>
      )}
      {!!appointment.acses_customer_communication?.events?.length && (
        <div className="AppointmentDetails-cards mt-15">
          <AcsesReceiverCommunicationLog appointment={appointment} />
        </div>
      )}
    </DetailsCard>
  );
};
